import _isEmpty from 'lodash.isempty';

import isEmail from 'validator/lib/isEmail';
import isPostalCode from 'validator/lib/isPostalCode';

const ERROR_CODES = {
  BOOKING_NUMBER_INVALID: 'BookingNumberInvalid',
  BOOKING_NUMBER_REQUIRED: 'BookingNumberRequired',
  CANCEL_FAILED: 'CancelFailed',
  EMAIL_REQUIRED: 'EmailRequired',
  FIRST_NAME_REQUIRED: 'FirstNameRequired',
  INVALID: 'invalid',
  INVALID_EMAIL_FORMAT: 'EmailInvalid',
  INVALID_EMAIL_LENGTH: 'EmailLengthInvalid',
  LAST_NAME_REQUIRED: 'LastNameRequired',
  PASSWORD_INVALID: 'PasswordInvalid',
  PASSWORD_MISMATCH: 'passwordsDoNotMatch',
  PASSWORD_REQUIRED: 'PasswordRequired',
  REQUIRED: 'required',
  VALID: '',
};

const ERROR_MESSAGES = {
  [ERROR_CODES.BOOKING_NUMBER_INVALID]: 'The booking number you entered does not meet the 7-10 digit requirement.',
  [ERROR_CODES.BOOKING_NUMBER_REQUIRED]: 'Booking number or Email address is required.',
  [ERROR_CODES.EMAIL_REQUIRED]: 'Email Address is required.',
  [ERROR_CODES.FIRST_NAME_REQUIRED]: 'First Name is required.',
  [ERROR_CODES.INVALID_EMAIL_FORMAT]:
    'Email must be in the following format: aa@aa.aaa and may only contain a-z, 0-9, dash, underscore, or period.',
  [ERROR_CODES.LAST_NAME_REQUIRED]: 'Last Name is required.',
  [ERROR_CODES.PASSWORD_INVALID]: 'Your password must be between 8 and 64 characters.',
  [ERROR_CODES.PASSWORD_MISMATCH]: 'Passwords must match. Please try again.',
  [ERROR_CODES.PASSWORD_REQUIRED]: 'Password is required.',
  [ERROR_CODES.REQUIRED]: 'Required',
};

const {
  BOOKING_NUMBER_INVALID,
  INVALID,
  INVALID_EMAIL_FORMAT,
  // CANCEL_FAILED,
  INVALID_EMAIL_LENGTH,
  PASSWORD_INVALID,
  // PASSWORD_REQUIRED,
  PASSWORD_MISMATCH,
  // EMAIL_REQUIRED,
  REQUIRED,
  VALID,
} = ERROR_CODES;

// Helper validation functions
// const dateValidator = (format, error) => (value) => {
//   if (!value || moment(value, format, true).isValid()) {
//     return VALID;
//   }
//   return error;
// };

const lengthValidator =
  ({ error, max, min = 0, noValue }) =>
  (value) => {
    if (!value) {
      return noValue;
    }
    return value.length >= min && value.length <= max ? VALID : error;
  };

// General validators
const checked = (value = false) => (!value ? REQUIRED : VALID);

const email = (address = '') => (isEmail(address) ? VALID : INVALID_EMAIL_FORMAT);

const matchingPasswords = (value = '', allValues = {}) => {
  return value === allValues.password ? VALID : PASSWORD_MISMATCH;
};

const passwordInvalid = lengthValidator({
  error: PASSWORD_INVALID,
  max: 64,
  min: 8,
  noValue: VALID,
});

const passwordMatch = (value = '', allValues = {}) => {
  return value === allValues.newPassword ? VALID : PASSWORD_MISMATCH;
};

const required = (value = '') => (_isEmpty(value) ? REQUIRED : VALID);

const validAddressLine = (value) => {
  if (!value) {
    return VALID;
  }
  return value.match(/^[#,./\s\d\-A-Z]{2,30}$/i) ? VALID : INVALID;
};

const validAddressLineCheck = (error) => (value) => {
  if (!value) {
    return VALID;
  }
  return value.match(/^[#,./\s\d\-A-Z]{2,30}$/i) ? VALID : error;
};

const validBookingNumberOrEmail = (value) => {
  if (!value) {
    return VALID;
  }
  const NUMBERS_REGEX = /^\d+$/;
  const BOOKING_NUMBER_REGEX = /^\d{7,10}$/;
  if (value.match(NUMBERS_REGEX)) {
    return value.match(BOOKING_NUMBER_REGEX) ? VALID : BOOKING_NUMBER_INVALID;
  }
  return email(value);
};

const validCity = lengthValidator({
  error: INVALID,
  max: 20,
  min: 2,
  noValue: INVALID,
});

// ACH Validations

// ACH forms have the country at different locations,
// so this helper function handles both cases
const getCountryFromValues = (values) => {
  let { country } = values;

  if (!country) {
    const { account } = values;
    ({ country } = account);
  }

  return country;
};

// Credit card validations
const CREDIT_CARD_VALIDATIONS = [
  {
    code: 'AX',
    tokenExType: 'americanExpress',
  },
  {
    code: 'DI',
    tokenExType: 'discover',
  },
  {
    code: 'MC',
    tokenExType: 'masterCard',
  },
  {
    code: 'VV',
    firstSixMatch: '432358',
    tokenExType: 'visa',
  },
  {
    code: 'VI',
    tokenExType: 'visa',
  },
];

const validCreditCardType = (value, _allValues, { tokenExMetadata: { cardType, firstSix } }) => {
  if (!value || !cardType) {
    return VALID;
  }

  const { firstSixMatch, tokenExType } = CREDIT_CARD_VALIDATIONS.find(({ code }) => code === value);

  /*
  isValid = true, if:
  1) the selected card type matches the one returned by TokenEx
  AND
  2) if there are a first six numbers to match (Viking Visa format),
     that it matches the first six numbers entered into the TokenEx field
  */
  const isValid = cardType === tokenExType && (!firstSixMatch || firstSix === firstSixMatch);
  return isValid ? VALID : INVALID;
};

const validEmail = (value) => {
  if (!value) {
    return VALID;
  }
  return email(value);
};

const validExpiryDate = (value) => {
  if (!value) {
    return VALID;
  }

  const { month, year } = value;
  if (!(month && year)) {
    return VALID;
  }

  const monthNum = parseInt(month, 10);
  const yearNum = parseInt(year, 10);

  // Calculate century dynamically based on current year
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentCentury = Math.floor(currentYear / 100) * 100;

  // Try current century first
  let fullYear = currentCentury + yearNum;

  // If the result is way in the past (>80 years), assume next century
  if (fullYear < currentYear - 80) {
    fullYear += 100;
  }

  const expiryDate = new Date(fullYear, monthNum - 1, 1);
  const currentDate = new Date(now.getFullYear(), now.getMonth(), 1);
  return expiryDate >= currentDate ? VALID : INVALID;
};

const validGifEmail = (value) => {
  if (!value) {
    return VALID;
  }
  return email(value);
};

const validGifEmailLength = lengthValidator({
  error: INVALID_EMAIL_LENGTH,
  max: 64,
  noValue: VALID,
});

const validNameOnCard = (value) => (value.match(/^[ '\-A-Z]{2,50}$/i) ? VALID : INVALID);

const validPostalCode = (value, allValues) => {
  const country = getCountryFromValues(allValues);
  if (!country) {
    return INVALID;
  }

  return isPostalCode(value, country) ? VALID : INVALID;
};

const validTime = (value, allValues, props, name) => {
  const { times } = props;
  if (value) {
    const passengerNum = name.includes('1') ? 0 : 1;
    const time = times.find((slot) => slot.value === value);
    return time.passengerConflicts[passengerNum] ? INVALID : VALID;
  }
  return VALID;
};

export {
  checked,
  email,
  ERROR_CODES,
  ERROR_MESSAGES,
  matchingPasswords,
  passwordInvalid,
  passwordMatch,
  required,
  validAddressLine,
  validAddressLineCheck,
  validBookingNumberOrEmail,
  validCity,
  validCreditCardType,
  validEmail,
  validExpiryDate,
  validGifEmail,
  validGifEmailLength,
  validNameOnCard,
  validPostalCode,
  validTime,
};
